import * as React from "react";

import { Button, Grid, Table, InputOnChangeData, Input, Loader } from "semantic-ui-react";

import { AppState, OrgStructureDetailModel } from "@teamprevent/common/orgStructure/models";

import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { deleteOrgStructure, loadOrgStructures } from "../actions";
import { ADMIN_ROLES, Authorize } from "../../../auth";

import { useCustomerDetailsModel } from "../../customer/hooks";

import DeleteButton from "./DeleteButton";

import { Link } from "redux-little-router";

interface OwnProps {
    orgStructure?: OrgStructureDetailModel;
}

export const All: React.FunctionComponent<OwnProps> = (props: OwnProps) => {
    const dispatch = useDispatch();
    const path = useSelector((state: AppState) => state.router.pathname);
    const orgStructures = useSelector((state: AppState) => state.orgStructures);
    const customerId = useSelector((state: AppState) => state.router.params.customerId);
    const orgStructureOneId = useSelector((state: AppState) => state.router.params.orgStructureOneId);
    const orgStructureTwoId = useSelector((state: AppState) => state.router.params.orgStructureTwoId);
    const orgStructureThreeId = useSelector((state: AppState) => state.router.params.orgStructureThreeId);
    const orgStructureFourId = useSelector((state: AppState) => state.router.params.orgStructureFourId);
    const { customer, isLoaded: isCustomerLoaded } = useCustomerDetailsModel({ customerId }) as any;

    const [name, setName] = React.useState("");

    React.useEffect(() => {
        dispatch(loadOrgStructures({
            customerId,
            orgStructureOneId,
            orgStructureTwoId,
            orgStructureThreeId,
            orgStructureFourId
        }));
    }, [customerId, dispatch, orgStructureFourId, orgStructureOneId, orgStructureThreeId, orgStructureTwoId]);

    const onFilterValueChange = React.useCallback((_: any, { value }: InputOnChangeData) => {
        setName(value);
        dispatch(loadOrgStructures({
            customerId,
            orgStructureOneId,
            orgStructureTwoId,
            orgStructureThreeId,
            orgStructureFourId,
            name
        }));
    }, [customerId, dispatch, name, orgStructureFourId, orgStructureOneId, orgStructureThreeId, orgStructureTwoId]);

    const dispatchDelete = React.useCallback((_: string, orgStructureToDeleteId: string, returnPath: string) =>
        dispatch(deleteOrgStructure(customerId, orgStructureToDeleteId, returnPath)), [customerId, dispatch]);

    const renderLink = React.useCallback((orgStructureDetailModel: OrgStructureDetailModel) => {
        if (!(props.orgStructure && props.orgStructure.level >= 2)) {
            return (
                <Link href={`${path}/${orgStructureDetailModel.id}`}>
                    {orgStructureDetailModel.name}
                </Link>
            );
        }
        return (
            <>{orgStructureDetailModel.name}</>
        );
    }, [path, props.orgStructure]);

    const renderCreateButton = () => {
        if (!(props.orgStructure && props.orgStructure.level >= 3)) {
            return (
                <Authorize roles={[ADMIN_ROLES.SYSTEM_ADMIN]}>
                    <Button
                        icon="add"
                        content="Create"
                        as={Link}
                        href={`${path}/create`}
                    />
                </Authorize>
            );
        }
        return (
            <>Level {props.orgStructure && props.orgStructure.level + 1}</>
        );
    };

    const renderTitle = () => {
        return (
            <Grid container className="nomargintop">
                <Grid.Row>
                    <Grid.Column width={14}>
                        <h1>{props.orgStructure && props.orgStructure.name} Organisation Level 4</h1>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        );
    };

    const renderTable = () => {
        const currentOrgLevel = props.orgStructure ? (props.orgStructure.level + 1) : 0;
        let filteredOrgStructures = (orgStructures && orgStructures.filter(org =>
            org.customerId === customerId && org.level === currentOrgLevel)) ?? [];
        if (props.orgStructure) {
            filteredOrgStructures = filteredOrgStructures.filter(org => org.parentId === props.orgStructure.id);
        }
        if (name) {
            const nameLowercased = name.toLowerCase();
            filteredOrgStructures = filteredOrgStructures.filter(org => org.name && org.name.toLowerCase().includes(nameLowercased));
        }
        return (
            <Grid container className="nomargintop">
                <Grid.Row>
                    <Grid.Column width={16}>
                        <h1>{props.orgStructure && props.orgStructure.name} Organisation Levels</h1>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Table>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Name</Table.HeaderCell>
                                    <Table.HeaderCell>
                                        {customer.orgLevel1Billing && <>Billing</>}
                                    </Table.HeaderCell>
                                    <Table.HeaderCell />
                                </Table.Row>
                                <Table.Row>
                                    <Table.HeaderCell>
                                        <Input
                                            value={name}
                                            onChange={onFilterValueChange}
                                            icon="search"
                                            floated="right"
                                            placeholder="Search..."
                                            size="mini"
                                        />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell />
                                    <Table.HeaderCell />
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {filteredOrgStructures.map(o => (
                                    <Table.Row key={o.id}>
                                        <Table.Cell>
                                            {renderLink(o)}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {customer.orgLevel1Billing &&
                                                <>{o.billing ? "Yes" : "No"}</>}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Authorize roles={[ADMIN_ROLES.SYSTEM_ADMIN]}>
                                                <Button
                                                    icon="pencil"
                                                    content="Edit"
                                                    floated="right"
                                                    as={Link}
                                                    href={`${path}/${o.id}/edit`}
                                                />
                                                <DeleteButton
                                                    customerId={customerId}
                                                    id={o.id}
                                                    path={path}
                                                    delete={dispatchDelete}
                                                />
                                            </Authorize>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </Grid.Column>
                </Grid.Row>
                <Grid className="Row">
                    <Grid.Column textAlign="right">
                        {renderCreateButton()}
                    </Grid.Column>
                </Grid>
            </Grid>
        );
    };

    if (!isCustomerLoaded) {
        return <Loader active />;
    }

    if (!(props.orgStructure && props.orgStructure.level >= 3)) {
        return renderTable();
    } else {
        return renderTitle();
    }
};
